import { Clocking } from '../api/clockings'
import { WorkPeriod, WorkPeriodWithSummary } from '../api/work-periods'
import { formatTime } from './date'
import { WorkPeriodSummaryCounted } from '../../../backend/src/services/resources/work-period-summaries/work-period-summary.types'

// --- Clocking Role ---

/**
 * Semantic role of a clocking relative to a work period.
 * Pure data — no presentation. All label/color mapping derives from this.
 */
export type ClockingRole =
  | 'entry'
  | 'exit'
  | 'break-start'
  | 'break-end'
  | 'intermediate'
  | 'orphan'
  | 'wp-deleted'

/**
 * Determines the semantic role of a clocking.
 *
 * @param clocking - The clocking to classify
 * @param workPeriod - The WP context (defaults to clocking.workPeriod if populated)
 */
export function getClockingRole(
  clocking: Clocking,
  workPeriod?: WorkPeriod,
): ClockingRole {
  const wp = workPeriod ?? clocking.workPeriod

  if (!clocking.mission || !clocking.type) return 'orphan'
  if (!wp) return 'wp-deleted'
  if (wp.start.clocking?.item === clocking._id) return 'entry'
  if (wp.end.clocking?.item === clocking._id) return 'exit'

  const breakIndex = wp.breaksClockings.findIndex(bc => bc.item === clocking._id)
  if (breakIndex !== -1) {
    return breakIndex % 2 === 0 ? 'break-start' : 'break-end'
  }

  return 'intermediate'
}

// --- Role to label ---

const ROLE_LABELS: Record<ClockingRole, string> = {
  entry: 'Entrée',
  exit: 'Sortie',
  'break-start': 'Début pause',
  'break-end': 'Fin pause',
  intermediate: 'Intermédiaire',
  orphan: 'Orphelin',
  'wp-deleted': 'Journée supprimée',
}

export function roleToLabel(role: ClockingRole): string {
  return ROLE_LABELS[role]
}

// --- Clocking status enum (for list filter) ---

export enum ClockingStatusEnum {
  noMission = 'Orphelin',
  entry = 'Entrée',
  exit = 'Sortie',
  workPeriodDeleted = 'Journée supprimée',
  replaced = 'Intermédiaire',
}

export function roleToStatus(role: ClockingRole): ClockingStatusEnum {
  switch (role) {
    case 'entry':
      return ClockingStatusEnum.entry
    case 'exit':
      return ClockingStatusEnum.exit
    case 'break-start':
    case 'break-end':
    case 'intermediate':
      return ClockingStatusEnum.replaced
    case 'orphan':
      return ClockingStatusEnum.noMission
    case 'wp-deleted':
      return ClockingStatusEnum.workPeriodDeleted
  }
}

/** Convenience wrapper for clockings list Type column. */
export function getStatus(clocking: Clocking): ClockingStatusEnum {
  return roleToStatus(getClockingRole(clocking))
}

// --- Timeline ---

/**
 * Source type of a time event: how was this time determined?
 */
export type TimelineSource = 'theoretical' | 'clocked' | 'manual' | 'orphan' | 'break'

const SOURCE_LABELS: Record<TimelineSource, string> = {
  theoretical: 'Théorique',
  clocked: 'Pointage',
  manual: 'Correction',
  orphan: 'Pointage',
  break: 'Pointage',
}

export function sourceToLabel(source: TimelineSource): string {
  return SOURCE_LABELS[source]
}

export interface TimelineEvent {
  time: Date
  source: TimelineSource
  /** What this event represents: Entrée, Sortie, Début pause, Orphelin, etc. */
  role: string
  /** True when this event is the "counted" time for hours calculation */
  isCounted: boolean
  clocking?: Clocking
}

/**
 * Builds a chronologically sorted timeline of all time events for a WP + clockings.
 *
 * For each start/end: adds theoretical, clocked, and manual events (when they exist).
 * The one matching `summary.counted` is marked `isCounted: true`.
 */
export function buildClockingsTimeline(
  clockings: Clocking[],
  workPeriod?: WorkPeriod,
): TimelineEvent[] {
  const events: TimelineEvent[] = []
  const summary = (workPeriod as WorkPeriodWithSummary)?.summary

  if (workPeriod) {
    const startCounted: WorkPeriodSummaryCounted = summary?.start?.counted ?? null
    const endCounted: WorkPeriodSummaryCounted = summary?.end?.counted ?? null

    // Start: theoretical
    events.push({
      time: new Date(workPeriod.start.date),
      source: 'theoretical',
      role: "Début de l'horaire",
      isCounted: startCounted === 'theoretical',
    })

    // Start: clocked
    if (workPeriod.start.clocking) {
      events.push({
        time: new Date(workPeriod.start.clocking.date),
        source: 'clocked',
        role: 'Entrée',
        isCounted: startCounted === 'clocked',
      })
    }

    // Start: manual
    if (workPeriod.start.manualClocking) {
      events.push({
        time: new Date(workPeriod.start.manualClocking),
        source: 'manual',
        role: 'Entrée',
        isCounted: startCounted === 'manual',
      })
    }

    // End: theoretical
    events.push({
      time: new Date(workPeriod.end.date),
      source: 'theoretical',
      role: "Fin de l'horaire",
      isCounted: endCounted === 'theoretical',
    })

    // End: clocked
    if (workPeriod.end.clocking) {
      events.push({
        time: new Date(workPeriod.end.clocking.date),
        source: 'clocked',
        role: 'Sortie',
        isCounted: endCounted === 'clocked',
      })
    }

    // End: manual
    if (workPeriod.end.manualClocking) {
      events.push({
        time: new Date(workPeriod.end.manualClocking),
        source: 'manual',
        role: 'Sortie',
        isCounted: endCounted === 'manual',
      })
    }

    // Break clockings
    for (const bc of workPeriod.breaksClockings) {
      const isStart = workPeriod.breaksClockings.indexOf(bc) % 2 === 0
      events.push({
        time: new Date(bc.date),
        source: 'break',
        role: isStart ? 'Début pause' : 'Fin pause',
        isCounted: false,
      })
    }
  }

  for (const clocking of clockings) {
    const role = getClockingRole(clocking, workPeriod)
    // Skip clockings already represented by WP start/end/break entries above
    if (role === 'entry' || role === 'exit' || role === 'break-start' || role === 'break-end') {
      continue
    }
    // Skip clockings registered to a different WP — they belong to that WP's timeline
    if (role === 'intermediate' || role === 'wp-deleted') continue
    // Only orphans remain
    events.push({
      time: new Date(clocking.date),
      source: 'orphan',
      role: roleToLabel(role),
      isCounted: false,
      clocking,
    })
  }

  return events.sort((a, b) => a.time.getTime() - b.time.getTime())
}
